/**
 * Game times are stored as Eastern time strings (YYYY-MM-DDTHH:mm, no timezone suffix).
 * These helpers let the client display them correctly in the user's local timezone.
 */

/**
 * Parse a stored Eastern-time string and return a Date the browser will
 * display in the user's local timezone.
 *
 * e.g. "2024-03-15T19:00" stored as Eastern →
 *   Eastern user sees  7:00 PM
 *   Pacific user sees  4:00 PM
 */
export function parseGameTime(s: string): Date {
  if (!s) return new Date(NaN);
  // Parse as UTC reference so we get a Date object without any local offset
  const d = new Date(s + 'Z');
  if (isNaN(d.getTime())) return d;
  // Find how many ms separate UTC and Eastern at this instant
  const utcMs = new Date(d.toLocaleString('en-US', { timeZone: 'UTC' })).getTime();
  const etMs  = new Date(d.toLocaleString('en-US', { timeZone: 'America/New_York' })).getTime();
  return new Date(d.getTime() + (utcMs - etMs));
}

/**
 * Convert a stored Eastern-time string to a value suitable for a
 * datetime-local input shown to the user (converted to their local time).
 */
export function easternToLocalInput(s: string): string {
  if (!s) return '';
  const d = parseGameTime(s);
  if (isNaN(d.getTime())) return s.slice(0, 16);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

/**
 * Convert a datetime-local input value (user's local time) back to
 * an Eastern-time string for storage/submission.
 */
export function localInputToEastern(s: string): string {
  if (!s) return s;
  const d = new Date(s); // parsed as local time by the browser
  return new Intl.DateTimeFormat('sv', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).format(d).replace(' ', 'T').replace('T24:', 'T00:');
}
